<template>
  <b-modal
    id="login-modal"
    ref="login-modal"
    centered
    scrollable
    hide-header
    hide-footer
    @hidden="onHidden"
  >
    <b-modal ref="message-modal" hide-footer hide-header no-close-on-backdrop>
      <b-container>
        <b-row>
          {{ message }}
        </b-row>
        <b-row>
          <b-img :src="saveAsImg" fluid />
        </b-row>
        <b-row>
          <b-col class="text-center">
            <b-button variant="primary" pill @click="closeAndUpdate">
              Entendido!
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </b-modal>

    <!-- New-user celebration: confetti + the free uses they just unlocked per
         service. Rendered IN PLACE (the login modal stays open) after a Google
         sign-up (data.is_new_user), so it isn't torn down by a modal close. -->
    <div v-if="showCelebration" class="lw-root">
        <div class="lw-confetti" aria-hidden="true">
          <span
            v-for="(c, i) in confetti"
            :key="i"
            class="lw-piece"
            :style="{
              left: c.left,
              background: c.color,
              animationDelay: c.delay,
              animationDuration: c.dur,
            }"
          ></span>
        </div>
        <div class="lw-emoji">🎉</div>
        <h3 class="lw-title">
          ¡Bienvenido/a a <strong>Utopie</strong>!
        </h3>
        <p class="lw-sub">
          Tu cuenta gratis ya está lista. Esto es lo que puedes usar
          <strong>sin costo</strong>:
        </p>
        <div class="lw-grid">
          <div v-for="s in welcomeServices" :key="s.key" class="lw-card">
            <img :src="s.icon" alt="" />
            <strong>{{ s.uses }}</strong>
            <span>{{ s.label }}</span>
          </div>
        </div>
        <button type="button" class="lw-cta" @click="finishCelebration">
          ¡Empezar a explorar!
        </button>
    </div>

    <div v-else class="lm-root">
      <button
        type="button"
        class="lm-close"
        aria-label="Cerrar"
        @click="close"
      >
        ×
      </button>

      <div class="lm-hero">
        <div class="lm-logo"></div>
        <span class="lm-badge">✦ Pruébalo gratis</span>
        <h3 class="lm-title">
          Te damos la bienvenida a <strong>Utopie</strong>
        </h3>
        <p class="lm-sub">
          {{
            is_registering
              ? "Crea tu cuenta gratis y empieza a usar todos nuestros servicios."
              : "Ingresa o crea tu cuenta gratis y prueba todos nuestros servicios."
          }}
        </p>
      </div>

      <div class="lm-card">
        <!-- Google sign-in: the recommended one-click path for new users. Its
             width is computed from the container (see googleWidth) so it never
             runs off-screen on mobile. A single button is rendered into this ref
             for both the login and register states. -->
        <!-- El aviso del navegador interno. Va ARRIBA del boton de Google y
             no en su lugar: si la hipotesis es falsa, el boton tiene que
             seguir ahi y funcionando. Se enciende con
             AVISO_NAVEGADOR_INTERNO en services/origen.js. -->
        <div v-if="mostrarAvisoNavegador" class="lm-aviso">
          <p class="lm-aviso-texto">
            Instagram abre las páginas en su propio navegador y Google no
            permite iniciar sesión ahí. Toca <strong>⋯</strong> arriba a la
            derecha y elige <strong>Abrir en Safari</strong> (o en Chrome).
          </p>
          <div class="lm-aviso-acciones">
            <button
              type="button"
              class="lm-aviso-boton"
              @click="copiarEnlace"
            >
              {{ copiado ? "¡Enlace copiado!" : "Copiar enlace" }}
            </button>
            <a
              v-if="esAndroid"
              class="lm-aviso-boton"
              :href="enlaceIntent"
              @click="avisarIntent"
            >
              Abrir en Chrome
            </a>
          </div>
        </div>

        <div class="lm-google-wrap">
          <!-- El contenedor del botón de Google NO se esconde nunca, tampoco
               mientras carga: GIS no dibuja en algo que no esté visible y con
               tamaño, así que quitarlo mientras se espera es asegurarse de que
               el botón no aparezca cuando la librería llegue. Lo que se ve
               entretanto va ENCIMA, en el mismo hueco y del mismo ancho, para
               que nada salte cuando el botón entra. -->
          <div class="lm-google-slot">
            <div ref="google" class="lm-google"></div>
            <div v-if="estadoGoogle !== 'listo'" class="lm-google-estado">
              <span v-if="estadoGoogle === 'cargando'">Cargando Google…</span>
              <template v-else>
                <span class="lm-google-fallo">
                  No pudimos cargar el inicio de sesión de Google
                </span>
                <button
                  type="button"
                  class="lm-google-reintentar"
                  @click="reintentar"
                >
                  Reintentar
                </button>
              </template>
            </div>
          </div>
          <p class="lm-google-hint">Rápido y seguro · sin crear contraseñas</p>
          <!-- La aceptacion, donde ocurre. El alta con Google no tiene un paso
               intermedio donde poner una casilla: la persona aprieta el boton
               y ya tiene cuenta. Asi que el aviso va debajo del boton, que es
               lo que hace todo el mundo, y el backend anota version y fecha
               en `user.data['terminos']` al crear la cuenta.

               Las dos paginas las sirve Django, no este SPA: se abren en
               pestana nueva para no tirar abajo el modal a medio login. -->
          <p class="lm-legal">
            Al continuar aceptas los
            <a href="/terminos/" target="_blank" rel="noopener noreferrer">
              Términos y condiciones
            </a>
            y la
            <a href="/privacidad/" target="_blank" rel="noopener noreferrer">
              Política de privacidad
            </a>.
          </p>
        </div>

        <!-- Returning users: email + password -->
        <template v-if="!is_registering">
          <div class="lm-or"><span>o con tu correo</span></div>
          <b-form @submit.prevent="login({ email: email, password: password })">
            <b-form-group class="lm-group" label-for="dropdown-form-email">
              <b-form-input
                v-on:keyup.enter="login({ email: email, password: password })"
                type="email"
                placeholder="Correo electrónico"
                v-model="email"
              ></b-form-input>
            </b-form-group>
            <b-form-group class="lm-group" label-for="dropdown-form-password">
              <b-form-input
                v-on:keyup.enter="login({ email: email, password: password })"
                type="password"
                placeholder="Contraseña"
                v-model="password"
              ></b-form-input>
            </b-form-group>
            <div class="lm-forgot">
              <a href="/accounts/password_reset/">Olvidé mi contraseña</a>
            </div>
            <b-button
              v-if="!$store.state.login.token"
              class="lm-submit"
              variant="primary"
              block
              @click="login({ email: email, password: password })"
            >
              Iniciar sesión
            </b-button>
          </b-form>
        </template>

        <!-- Manual registration form (kept; reachable when manualRegister) -->
        <template v-else-if="manualRegister">
          <div class="lm-or"><span>con tus datos</span></div>
          <b-form @submit.prevent="registerAux">
            <b-form-group class="lm-group" label-for="dropdown-form-r_first_name">
              <b-form-input
                placeholder="Nombre"
                v-on:keyup.enter="registerAux"
                v-model="r_first_name"
                :state="r_first_name.length > 0"
              ></b-form-input>
            </b-form-group>
            <b-form-group class="lm-group" label-for="dropdown-form-r_last_name">
              <b-form-input
                placeholder="Apellido"
                v-on:keyup.enter="registerAux"
                v-model="r_last_name"
                :state="r_last_name.length > 0"
              ></b-form-input>
            </b-form-group>
            <b-form-group class="lm-group" label-for="dropdown-form-r_email">
              <b-form-input
                placeholder="Correo"
                v-on:keyup.enter="registerAux"
                type="email"
                v-model="r_email"
                :state="r_email.length > 0"
              ></b-form-input>
            </b-form-group>
            <b-form-group class="lm-group" label-for="dropdown-form-r_password">
              <b-form-input
                type="password"
                v-on:keyup.enter="registerAux"
                placeholder="Contraseña"
                v-model="r_password"
                :state="r_password.length > 0"
              ></b-form-input>
            </b-form-group>
            <b-form-group class="lm-group" label-for="dropdown-form-r_password2">
              <b-form-input
                type="password"
                placeholder="Repetir contraseña"
                v-on:keyup.enter="registerAux"
                v-model="r_password2"
                :state="r_password2.length > 0"
              ></b-form-input>
            </b-form-group>
            <b-button
              class="lm-submit"
              variant="primary"
              block
              @click="registerAux"
            >
              Registrarse
            </b-button>
          </b-form>
        </template>

        <!-- Salida para quien ya tiene cuenta. El estado de registro no es un
             formulario aparte: `manualRegister` está en false y nadie lo
             enciende, así que muestra el mismo botón de Google y nada más.
             Sin este enlace, quien llega desde un CTA de "crear mi cuenta" y
             resulta que ya está registrado se queda sin campo de correo, sin
             "olvidé mi contraseña" y sin forma de volver al login. -->
        <p v-if="is_registering" class="lm-switch">
          ¿Ya tienes cuenta?
          <button type="button" @click="set_register(false)">
            Inicia sesión
          </button>
        </p>
      </div>
    </div>
  </b-modal>
</template>
<script>
import { authMixin } from "../../../mixins/authMixin";
import auth from "@/services/authService";
import userService from "@/services/userService";
import { eventoLogin, motivoDeFallo, trackSignUp } from "@/services/analytics";
import {
  AVISO_NAVEGADOR_INTERNO,
  navegadorInterno,
  origenDelAlta,
} from "@/services/origen";

//: Cuanto se espera a que llegue la libreria de Google antes de darla por no
//: cargada. Lo mismo que en GoogleTrialButton: 40 x 250 ms = 10 segundos.
const INTENTOS_GIS = 40;
const ESPERA_GIS_MS = 250;

//: Cuanto se le da a GIS para dejar su boton dibujado despues de aceptar el
//: `renderButton`. El iframe lo crea al vuelo, asi que esto es margen y no
//: espera: si al segundo largo no hay nada, no va a haber nada.
const ESPERA_BOTON_MS = 1500;

// Same icons as the landing-page service shortcuts, reused in the new-user
// celebration grid.
const WELCOME_ICONS = {
  eureka: require("@/assets/img/landing_page/Iconos/Ícono_Eureka copia@4x.png"),
  planif: require("@/assets/img/landing_page/Iconos/Ícono_planif clases copia@4x.png"),
  materials: require("@/assets/img/landing_page/Iconos/Ícono_materiales interactivos copia@4x.png"),
  tests: require("@/assets/img/landing_page/Iconos/Corregir_TEST.png"),
  docs: require("@/assets/img/landing_page/Iconos/Documentos_PIE.png"),
};
const CONFETTI_COLORS = [
  "#e84855",
  "#20b2aa",
  "#f8ac04",
  "#208ac6",
  "#b76629",
  "#c6588d",
];

export default {
  mixins: [authMixin],
  data: function () {
    return {
      manualRegister: false,
      email: "",
      password: "",
      r_email: "",
      r_password: "",
      r_password2: "",
      r_first_name: "",
      r_last_name: "",
      is_registering: false,
      auth2: null,
      clientID: process.env.VUE_APP_GOOGLE_ID,
      message: "",
      isOpen: false,
      saveAsImg: require("../../../assets/img/save_as.jpg"),
      welcomeUses: null,
      confetti: [],
      showCelebration: false,
      copiado: false,
      // "cargando" | "listo" | "fallo". Lo que se ve en el hueco del boton de
      // Google mientras no hay boton de Google.
      estadoGoogle: "cargando",
    };
  },
  computed: {
    // Solo con el interruptor encendido y solo dentro del navegador de otra
    // app. Las dos condiciones, siempre: encendido no significa "para todos".
    mostrarAvisoNavegador() {
      return AVISO_NAVEGADOR_INTERNO && navegadorInterno();
    },
    esAndroid() {
      const ua = (window.navigator && window.navigator.userAgent) || "";
      return /Android/i.test(ua);
    },
    /**
     * Un `intent://` que abre Chrome directo, solo en Android.
     *
     * OJO CON EL HASH: un intent no puede llevar fragmento —el `#` es del
     * propio Intent— y las rutas del SPA viven justamente ahi. Chrome abre la
     * entrada del SPA, no la demo. Por eso el boton de copiar copia la URL
     * ENTERA y este enlace es el atajo, no el camino bueno; y por eso la URL
     * completa va ademas como `browser_fallback_url`, que es lo que Chrome usa
     * si no puede resolver el intent.
     */
    enlaceIntent() {
      const l = window.location;
      return (
        "intent://" +
        l.host +
        l.pathname +
        l.search +
        "#Intent;scheme=https;package=com.android.chrome;" +
        "S.browser_fallback_url=" +
        encodeURIComponent(l.href) +
        ";end"
      );
    },
    // The free uses unlocked per service, built from the login response.
    welcomeServices() {
      const u = this.welcomeUses;
      if (!u) return [];
      const list = [
        { key: "planif", label: "Planificaciones", uses: u.planifications, icon: WELCOME_ICONS.planif },
        { key: "materials", label: "Pruebas Informales", uses: u.materials, icon: WELCOME_ICONS.materials },
        { key: "tests", label: "Corrección de Tests", uses: u.tests, icon: WELCOME_ICONS.tests },
        { key: "docs", label: "Documentación PIE", uses: u.documents, icon: WELCOME_ICONS.docs },
      ];
      if (u.eureka > 0) {
        list.unshift({ key: "eureka", label: "Eureka", uses: u.eureka, icon: WELCOME_ICONS.eureka });
      }
      return list.filter((s) => s.uses != null);
    },
  },
  created() {},
  mounted() {
    // Re-render the Google button on resize/rotation so its (fixed) pixel width
    // keeps matching the container and never overflows the screen.
    this._onResize = () => {
      if (!this.isOpen) return;
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.renderGoogle(), 200);
    };
    window.addEventListener("resize", this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
    clearTimeout(this._resizeTimer);
    clearTimeout(this._esperaGis);
    clearTimeout(this._revision);
  },
  watch: {
    is_registering() {
      this.start2();
    },
  },
  methods: {
    onHidden() {
      this.isOpen = false;
      this.showCelebration = false;
      this.welcomeUses = null;
      this.$emit("hidden");
    },
    closeAndUpdate() {
      userService.alertMessageSeen();
      this.$refs["message-modal"].hide();
    },
    // New-user celebration: confetti + free uses per service, shown in place.
    showWelcome(data) {
      this.welcomeUses = {
        planifications: data.planificationsUses,
        materials: data.materialsUses,
        tests: data.testsUses,
        documents: data.documentsUses,
        eureka: data.eurekaUses,
      };
      this.confetti = this.buildConfetti();
      this.showCelebration = true;
    },
    finishCelebration() {
      this.showCelebration = false;
      this.close();
    },
    buildConfetti() {
      const pieces = [];
      for (let i = 0; i < 40; i += 1) {
        pieces.push({
          left: Math.floor(Math.random() * 100) + "%",
          color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          delay: (Math.random() * 2).toFixed(2) + "s",
          dur: (2.4 + Math.random() * 1.8).toFixed(2) + "s",
        });
      }
      return pieces;
    },
    handleCredentialResponse(data) {
      // El trio login_attempt/success/failure existia solo para el camino de
      // correo y contrasena (authMixin). Google entraba mudo: solo emitia
      // `sign_up`, y solo en el alta, asi que todo el que YA tenia cuenta
      // entraba sin dejar rastro — y Google es el camino principal.
      eventoLogin("login_attempt", { method: "google" });
      this.authGoogle(data.credential);
    },
    // Solo viaja el id_token. Antes se decodificaba el JWT aca y se mandaban
    // email/sub sueltos: el backend confiaba en esos campos, asi que cualquiera
    // podia pedir la sesion de cualquier cuenta mandando otro correo. Ahora el
    // backend valida firma, exp y aud contra nuestro client id.
    authGoogle(idToken) {
      // Sin el colegio guardado: el alta no mete a nadie en un colegio, y lo
      // que hay en el navegador lo puede escribir cualquiera.
      var payload = {
        id_token: idToken,
        // Lo unico que el servidor no puede averiguar solo: el producto y la
        // ruta viven despues del `#`. Con esto la fila de OrigenCuenta sabe
        // desde que demo se creo la cuenta.
        origen: origenDelAlta(),
      };
      auth.authenticateGoogle(payload).then(
        (data) => {
          if (data.token) {
            eventoLogin("login_success", { method: "google" });
            this.$store.commit("login/setUser", data);
            if (data.is_new_user) {
              // La conversión que faltaba medir: sin esto un registro que
              // viene de una búsqueda queda contado como una visita más.
              trackSignUp("google");
              // Keep the modal open and swap its content to the celebration.
              this.showWelcome(data);
            } else {
              this.close();
              if (data.message) {
                this.message = data.message;
                this.$refs["message-modal"].show();
              }
            }
          } else {
            eventoLogin("login_failure", {
              method: "google",
              reason: "no_token",
            });
            this.$toasted.error("Revisa tu correo o contraseña.");
            this.$store.commit("login/setUserEmpty");
            // TODO: add form error
          }
          // eslint-disable-next-line
        },
        (error) => {
          // Antes todo lo que no fuera un 403 se contaba como "server_error",
          // asi que un telefono sin red quedaba anotado como una caida
          // nuestra. El motivo sale del error, y si el error no dice nada sale
          // "desconocido": nunca vacio.
          eventoLogin("login_failure", {
            method: "google",
            reason: motivoDeFallo(error),
          });
          // Y sin esta guarda, un error de red reventaba aca dentro —no hay
          // `response` que leer— y se comia su propio aviso.
          const dice = error.response && error.response.data;
          if (dice && dice.message) this.$toasted.error(dice.message);
          else {
            console.log(error);
            this.$toasted.error(
              "Ha ocurrido un error en el servidor, intenta más tarde."
            );
            this.$store.commit("login/setUserEmpty");
          }
        }
      );
    },
    registerAux: function () {
      this.register({
        first_name: this.r_first_name,
        last_name: this.r_last_name,
        email: this.r_email,
        password1: this.r_password,
        password2: this.r_password2,
      });
    },
    clean: function () {
      this.email = "";
      this.password = "";
      this.r_email = "";
      this.r_password = "";
      this.r_password2 = "";
      this.r_first_name = "";
      this.r_last_name = "";
      this.manualRegister = false;
    },
    login: function (payload) {
      this.authenticate_password(payload, this);
    },
    deleteAllCookies() {
      const cookies = document.cookie.split(";");

      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT";
      }
    },
    showPrompt() {
      window.google.accounts.id.prompt();
    },
    start2: async function () {
      // Salvo que ya haya uno dibujado: `start2` corre otra vez al pasar de
      // entrar a registrarse, y ahi taparle el boton a alguien un segundo para
      // decirle que se esta cargando algo que ya cargo es solo un parpadeo.
      if (!this.hayBoton()) this.estadoGoogle = "cargando";
      try {
        // Antes esto era un `return` mudo: si la libreria no habia llegado, el
        // hueco del boton se quedaba vacio y no lo sabia nadie. Es la mitad de
        // la sospecha de los navegadores internos —el otro caso es que el
        // boton se dibuje y no responda— y sin este evento las dos se ven
        // igual en el informe: un muro sin ningun intento.
        if (!(await this.esperarGis())) {
          // Salvo que ya no haya nadie mirando: `esperarGis` tambien devuelve
          // `false` cuando el modal se cerro durante la espera, y eso no es un
          // fallo de Google sino alguien que se fue.
          if (this.isOpen) this.rendirse("gis_no_disponible");
          return;
        }
        window.google.accounts.id.initialize({
          client_id: process.env.VUE_APP_GOOGLE_ID,
          callback: this.handleCredentialResponse,
          cancel_on_tap_outside: false,
          ux_mode: "popup",
        });
        await new Promise((r) => setTimeout(r, 1000));
        this.renderGoogle();
      } catch (error) {
        console.log(error);
        this.rendirse("gis_no_dibuja");
      }
    },
    /**
     * Espera a que llegue la libreria de Google. `false` si no llego.
     *
     * La carga public/index.html, asi que al abrir el modal recien entrado a
     * la pagina puede no estar todavia. Se rinde a los 10 segundos: pasado
     * eso, el problema no es la conexion.
     */
    esperarGis() {
      return new Promise((resolve) => {
        let intentos = 0;
        const probar = () => {
          if (!this.isOpen) return resolve(false);
          if (window.google && window.google.accounts && window.google.accounts.id) {
            return resolve(true);
          }
          intentos += 1;
          if (intentos > INTENTOS_GIS) return resolve(false);
          this._esperaGis = setTimeout(probar, ESPERA_GIS_MS);
        };
        probar();
      });
    },
    /**
     * Se avisa que el login con Google no llego a empezar. Una vez por
     * apertura: `start2` corre otra vez al pasar de entrar a registrarse, y
     * sin la marca el mismo problema saldria contado dos veces.
     */
    avisar(motivo) {
      if (this._avisado) return;
      this._avisado = true;
      eventoLogin("login_failure", { method: "google", reason: motivo });
    },
    /** Y ademas se dice en pantalla, que es donde hay alguien esperando. */
    rendirse(motivo) {
      this.estadoGoogle = "fallo";
      this.avisar(motivo);
    },
    /**
     * Volver a intentarlo sin recargar la pagina.
     *
     * Empieza por la espera y no por el dibujo: si la libreria nunca llego, no
     * hay nada que redibujar. Y borra la marca a proposito — un reintento a
     * mano es un intento nuevo, y si vuelve a fallar ese segundo fallo cuenta;
     * lo que no puede contarse dos veces es el mismo fallo automatico.
     */
    reintentar() {
      this._avisado = false;
      this.start2();
    },
    /**
     * Alguien apreto el boton de Google. Lo dice la propia libreria.
     *
     * Antes esto se adivinaba: la pagina pierde el foco y el foco queda en el
     * iframe de Google, luego hubo un clic. Se disparaba igual llegando con el
     * teclado, y llegar con el teclado no es apretar. `renderButton` acepta
     * `click_listener`, que es este mismo dato dicho desde adentro del iframe,
     * donde el clic si se ve.
     *
     * Se cuenta cada clic y no solo el primero: si alguien aprieta tres veces
     * es porque no paso nada, y ese numero —clics por persona— es justo el que
     * dice que el boton no responde.
     */
    contarElClic() {
      eventoLogin("login_click", { method: "google" });
    },
    /** ¿Quedo dibujado el boton? GIS lo pone dentro de un iframe suyo. */
    hayBoton() {
      const el = this.$refs["google"];
      return !!(el && el.querySelector && el.querySelector("iframe"));
    },
    /**
     * El tercer modo de no tener boton, y el unico que era invisible.
     *
     * Los otros dos —la libreria no llego, `renderButton` tiro excepcion— ya
     * se avisan. Este es el caso en que GIS acepta el render, no se queja, y
     * el hueco se queda vacio igual.
     *
     * El hueco solo se tapa si no quedo NADA dibujado. Si algun dia GIS
     * dejara de usar un iframe, el boton seguiria ahi funcionando y taparlo
     * por una comprobacion nuestra seria romper el login para arreglar una
     * medicion; el evento sale igual, y un salto brusco de ese numero al 100%
     * significa que cambio el marcado, no que se rompio el boton.
     */
    revisarElBoton() {
      if (!this.isOpen || this.hayBoton()) return;
      const el = this.$refs["google"];
      if (el && el.childElementCount) {
        this.avisar("gis_no_boton");
        return;
      }
      this.rendirse("gis_no_boton");
    },
    /**
     * Copia la URL con su query: sin ella se pierde el `?invita=` de una
     * invitacion y el `fbclid` del anuncio, que es de donde sale la
     * atribucion.
     */
    copiarEnlace() {
      const url = window.location.href;
      const listo = () => {
        this.copiado = true;
        setTimeout(() => {
          this.copiado = false;
        }, 2500);
        eventoLogin("aviso_navegador_interno", { accion: "copiar" });
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(listo, () => this.copiarAPulso(url, listo));
        return;
      }
      // El navegador de Instagram no siempre trae la API del portapapeles.
      this.copiarAPulso(url, listo);
    },
    copiarAPulso(url, listo) {
      try {
        const campo = document.createElement("textarea");
        campo.value = url;
        campo.setAttribute("readonly", "");
        campo.style.position = "fixed";
        campo.style.opacity = "0";
        document.body.appendChild(campo);
        campo.select();
        document.execCommand("copy");
        document.body.removeChild(campo);
        listo();
      } catch (e) {
        // Si no se puede copiar, el aviso sigue diciendo como salir a mano.
      }
    },
    avisarIntent() {
      eventoLogin("aviso_navegador_interno", { accion: "intent" });
    },
    renderGoogle() {
      const el = this.$refs["google"];
      if (!el || !window.google || !window.google.accounts) return;
      // Re-rendering (resize / state change) would stack buttons; clear first.
      el.innerHTML = "";
      window.google.accounts.id.renderButton(el, {
        text: this.is_registering ? "signup_with" : "continue_with",
        size: "large",
        width: this.googleWidth(),
        theme: "outline",
        logo_alignment: "left",
        shape: "pill",
        locale: "es",
        click_listener: this.contarElClic,
      });
      // Optimista a proposito: si el boton se dibujo, el aviso de carga se va
      // ya y no un segundo y medio despues. La revision de abajo lo devuelve
      // si resulta que no habia boton.
      this.estadoGoogle = "listo";
      clearTimeout(this._revision);
      this._revision = setTimeout(this.revisarElBoton, ESPERA_BOTON_MS);
    },
    googleWidth() {
      // GIS requires a fixed pixel width (max 400). Derive it from the
      // container (and clamp by the viewport) so it never overflows on mobile.
      const el = this.$refs["google"];
      let w = 320;
      if (el) {
        w =
          el.clientWidth ||
          (el.parentElement && el.parentElement.clientWidth) ||
          320;
      }
      const maxByScreen = (window.innerWidth || 400) - 56;
      return Math.max(200, Math.min(400, Math.floor(w), maxByScreen));
    },
    open: function (register) {
      // if (!this.$store.state.login.token) {
      //   this.deleteAllCookies();
      // }
      this.clean();
      this.showCelebration = false;
      if (register) {
        this.is_registering = true;
      } else {
        this.is_registering = false;
      }
      this.isOpen = true;
      // Una sola vez por apertura, como pide el embudo: si se cuenta dos veces
      // el mismo fallo, la tasa de "el boton no aparecio" queda inflada.
      this._avisado = false;
      this.copiado = false;
      this.estadoGoogle = "cargando";
      this.$refs["login-modal"].show();
      this.start2();
    },
    close: function () {
      this.isOpen = false;
      this.$refs["login-modal"].hide();
    },
    set_register: function (register) {
      this.is_registering = register;
      // TODO reset errors
    },
    register: function (payload) {
      this.register_user(payload, this);
    },
  },
};
</script>

<style lang="scss">
/* ===== Login modal shell ===== */
#login-modal___BV_modal_content_ {
  border: 0;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 28px 70px rgba(31, 18, 40, 0.28);
}
#login-modal___BV_modal_body_ {
  padding: 0;
}

#login-modal .form-control:focus {
  border-color: $primary !important;
  box-shadow: 0 0 0 3px rgba(198, 88, 141, 0.18) !important;
}

.lm-root {
  position: relative;
  font-family: "Ubuntu", "Segoe UI", system-ui, sans-serif;
  color: #2c2536;
}
.lm-close {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 3;
  width: 30px;
  height: 30px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  color: #7a6b78;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}
.lm-close:hover {
  background: #fff;
  color: #2c2536;
}

/* ===== Hero ===== */
.lm-hero {
  text-align: center;
  padding: 26px 26px 22px;
  background: linear-gradient(150deg, #fbeaf3 0%, #eef0ff 100%);
  border-bottom: 1px solid rgba(198, 88, 141, 0.12);
}
.lm-logo {
  width: 100%;
  height: 46px;
  margin-bottom: 12px;
  background: url(../../../assets/img/isotipo_utopie.svg) no-repeat center;
  background-size: contain;
}
.lm-badge {
  display: inline-block;
  margin-bottom: 10px;
  padding: 4px 12px;
  border-radius: 999px;
  background: #fff;
  color: $primary;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  box-shadow: 0 4px 12px rgba(198, 88, 141, 0.14);
}
.lm-title {
  margin: 0 0 6px;
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1.2;
  color: #241b2e;
}
.lm-title strong {
  color: $primary;
}
.lm-sub {
  margin: 0 auto;
  max-width: 330px;
  font-size: 0.9rem;
  line-height: 1.35;
  color: #6f6577;
}

/* ===== Card ===== */
.lm-card {
  padding: 20px 24px 18px;
}
/* El aviso del navegador interno. Los mismos tonos de la tarjeta y ni un
   color nuevo: es una explicacion, no una alarma — el boton de Google sigue
   ahi debajo y puede funcionar igual. */
.lm-aviso {
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1.5px solid #e7e1ee;
  border-radius: 12px;
  background: #faf8fc;
}
.lm-aviso-texto {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: #6f6577;
}
.lm-aviso-acciones {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
}
.lm-aviso-boton,
.lm-google-reintentar {
  padding: 7px 14px;
  border: 1.5px solid #e7e1ee;
  border-radius: 10px;
  background: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  color: $primary;
  cursor: pointer;
  text-decoration: none;
}
.lm-aviso-boton:hover,
.lm-aviso-boton:focus-visible,
.lm-google-reintentar:hover,
.lm-google-reintentar:focus-visible {
  border-color: $primary;
  color: $primary;
  text-decoration: none;
}

.lm-google-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}
/* El hueco del boton. Lo que se ve mientras no hay boton va encima y no en su
   lugar: GIS necesita este contenedor visible y con tamano para dibujar. */
.lm-google-slot {
  position: relative;
  width: 100%;
  min-height: 44px;
}
.lm-google-estado {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #fff;
  font-size: 0.82rem;
  color: #9b8fa3;
}
.lm-google-fallo {
  color: #6f6577;
  line-height: 1.35;
  text-align: center;
}
.lm-google {
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 44px;
}
.lm-google > div {
  max-width: 100%;
}
.lm-google-hint {
  margin: 8px 0 0;
  font-size: 0.72rem;
  color: #9b8fa3;
}

/* La linea de aceptacion. Mismo tamano y mismo gris que el hint de arriba: es
   informacion de la misma clase, no un segundo aviso que compita con el boton.
   Los enlaces solo se subrayan, sin color propio, para que la linea siga
   leyendose como una sola frase.

   `line-height` apretado a proposito: en un telefono de 360 px esta frase
   ocupa tres renglones, y el boton de Google va ARRIBA — lo que empujaria
   fuera de pantalla seria el formulario de correo, no el boton. */
.lm-legal {
  margin: 6px 0 0;
  font-size: 0.72rem;
  line-height: 1.35;
  color: #9b8fa3;
}

.lm-legal a {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 0.15em;
}

.lm-or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0 14px;
  color: #b3a9bb;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.lm-or:before,
.lm-or:after {
  content: "";
  flex: 1;
  height: 1px;
  background: #ece7f1;
}

.lm-group {
  margin-bottom: 12px;
}
.lm-group .form-control {
  height: 46px;
  border-radius: 12px;
  border: 1.5px solid #e7e1ee;
  background: #faf8fc;
  padding: 0 14px;
  font-size: 0.92rem;
}
.lm-forgot {
  text-align: right;
  margin: -4px 0 12px;
}
.lm-forgot a {
  font-size: 0.78rem;
  font-weight: 600;
  color: $primary;
}

/* El puente entre los dos estados del modal. Va como texto y no como boton:
   el unico boton de esta tarjeta tiene que seguir siendo el de entrar. */
.lm-switch {
  margin: 14px 0 0;
  text-align: center;
  font-size: 0.82rem;
  color: #9b8fa3;
}
.lm-switch button {
  padding: 0;
  border: 0;
  background: none;
  font-size: inherit;
  font-weight: 700;
  color: $primary;
  cursor: pointer;
}
.lm-switch button:hover,
.lm-switch button:focus-visible {
  text-decoration: underline;
}
.lm-submit {
  height: 48px;
  border-radius: 12px;
  font-weight: 800;
  font-size: 0.98rem;
  box-shadow: 0 12px 24px rgba(198, 88, 141, 0.22);
}

.lm-perks {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 14px;
  margin: 16px 0 4px;
  padding: 0;
}
.lm-perks li {
  position: relative;
  padding-left: 18px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #6f6577;
}
.lm-perks li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #2bb673;
  font-weight: 900;
}

@media (max-width: 480px) {
  .lm-hero {
    padding: 18px 16px 14px;
  }
  .lm-logo {
    height: 38px;
    margin-bottom: 8px;
  }
  .lm-badge {
    margin-bottom: 8px;
  }
  .lm-title {
    font-size: 1.18rem;
  }
  .lm-sub {
    font-size: 0.84rem;
  }
  .lm-card {
    padding: 16px 16px 14px;
  }
  .lm-or {
    margin: 12px 0 10px;
  }
  .lm-google-hint,
  .lm-legal {
    font-size: 0.68rem;
  }
  .lm-group {
    margin-bottom: 10px;
  }
  .lm-group .form-control {
    height: 44px;
  }
  .lm-submit {
    height: 46px;
  }
  .lm-perks {
    margin: 12px 0 2px;
    gap: 6px 12px;
  }
}

/* Short viewports (e.g. landscape phones): keep the body scrollable but trim
   the hero so the form stays reachable. */
@media (max-height: 640px) {
  .lm-hero {
    padding: 16px 18px 12px;
  }
  .lm-logo {
    height: 34px;
    margin-bottom: 6px;
  }
  .lm-sub {
    display: none;
  }
}

/* ===== New-user celebration ===== */
.lw-root {
  position: relative;
  overflow: hidden;
  text-align: center;
  padding: 28px 24px 24px;
  font-family: "Ubuntu", Arial, sans-serif;
}
.lw-confetti {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}
.lw-piece {
  position: absolute;
  top: -16px;
  width: 8px;
  height: 14px;
  border-radius: 2px;
  opacity: 0;
  animation-name: lw-fall;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
@keyframes lw-fall {
  0% {
    transform: translateY(-20px) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  100% {
    transform: translateY(420px) rotate(540deg);
    opacity: 0;
  }
}
.lw-emoji {
  position: relative;
  z-index: 1;
  font-size: 2.6rem;
  line-height: 1;
  margin-bottom: 4px;
  animation: lw-pop 0.6s cubic-bezier(0.2, 1.5, 0.4, 1) both;
}
@keyframes lw-pop {
  0% {
    transform: scale(0);
  }
  70% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
.lw-title {
  position: relative;
  z-index: 1;
  margin: 0 0 6px;
  font-size: 1.4rem;
  font-weight: 800;
  color: #241b2e;
}
.lw-title strong {
  color: $primary;
}
.lw-sub {
  position: relative;
  z-index: 1;
  margin: 0 auto 16px;
  max-width: 340px;
  font-size: 0.9rem;
  color: #6f6577;
  line-height: 1.35;
}
.lw-sub strong {
  color: $primary;
}
.lw-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 18px;
}
.lw-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 8px;
  border-radius: 14px;
  background: #faf6fb;
  border: 1.5px solid rgba(198, 88, 141, 0.16);
}
.lw-card img {
  width: 40px;
  height: 40px;
  object-fit: contain;
  margin-bottom: 4px;
}
.lw-card strong {
  font-size: 1.3rem;
  font-weight: 800;
  color: $primary;
  line-height: 1;
}
.lw-card span {
  font-size: 0.74rem;
  font-weight: 700;
  color: #6f6577;
  line-height: 1.15;
}
.lw-cta {
  position: relative;
  z-index: 1;
  width: 100%;
  border: 0;
  border-radius: 12px;
  padding: 13px 18px;
  font-family: inherit;
  font-size: 0.98rem;
  font-weight: 800;
  color: #fff;
  background: $primary;
  box-shadow: 0 12px 24px rgba(198, 88, 141, 0.26);
  cursor: pointer;
  transition: filter 0.2s ease;
}
.lw-cta:hover {
  filter: brightness(1.06);
}

@media (prefers-reduced-motion: reduce) {
  .lw-piece,
  .lw-emoji {
    animation: none !important;
  }
}
</style>
